import React from "react";
import { useTranslation } from "react-i18next";
import { VerifyCheckIcon } from "./icons";
import { FlightFixture } from "./types";

export interface ConfirmedStepProps {
  fixture: FlightFixture;
  phoneNumber: string;
}

const TRIGGER_ICONS: Record<
  "arrival" | "departure",
  { glyph: string; danger?: boolean }[]
> = {
  arrival: [
    { glyph: "schedule" },
    { glyph: "check_circle" },
    { glyph: "cancel", danger: true },
  ],
  departure: [
    { glyph: "schedule" },
    { glyph: "flight_takeoff" },
    { glyph: "check_circle" },
    { glyph: "cancel", danger: true },
  ],
};

export const ConfirmedStep: React.FC<ConfirmedStepProps> = ({
  fixture,
  phoneNumber,
}) => {
  const { t } = useTranslation("flightAlerts");
  const isArrival = fixture.direction === "arrival";

  const subtextKey = isArrival
    ? "confirmed.subtextArrival"
    : "confirmed.subtextDeparture";
  const triggersKey = isArrival
    ? "confirmed.triggersArrival"
    : "confirmed.triggersDeparture";
  const selfCloseKey = isArrival
    ? "confirmed.selfCloseNoteArrival"
    : "confirmed.selfCloseNoteDeparture";

  const triggers = t(triggersKey, { returnObjects: true }) as string[];
  const triggerIcons = TRIGGER_ICONS[fixture.direction];

  return (
    <div className="flex flex-col gap-6">
      <VerifyCheckIcon className="h-16 w-16" />

      <div className="flex flex-col gap-3">
        <h2 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
          {t("confirmed.heading")}
        </h2>
        <p className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-60">
          {t(subtextKey, {
            phone: phoneNumber,
            flightNumber: fixture.flightNumber,
          })}
        </p>
      </div>

      <div className="flex flex-col gap-[14px] rounded-2xl border-2 border-ink-12 bg-card p-8">
        <span className="font-lexend text-[10px]/[12px] font-bold uppercase tracking-[0.6px] text-ink-40">
          {t("confirmed.triggersHeading")}
        </span>

        <ul className="flex flex-col gap-3">
          {triggers.map((trigger, i) => {
            const icon = triggerIcons[i];
            return (
              <li key={trigger} className="flex items-center gap-[10px]">
                <span
                  aria-hidden="true"
                  className={`font-material-symbols shrink-0 text-[20px] leading-none ${
                    icon?.danger ? "text-danger-100" : "text-ink-100"
                  }`}
                >
                  {icon?.glyph ?? "schedule"}
                </span>
                <span className="font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100">
                  {trigger}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="h-px w-full bg-ink-10" />

        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {t(selfCloseKey, { flightNumber: fixture.flightNumber })}
        </p>
      </div>
    </div>
  );
};
